import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import _ from 'lodash';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Button, Stack, Typography, Box, Card, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import { translate as t } from 'hooks/useTranslations';
import useUrlParams from 'hooks/useUrlParams';
import { AppContext } from 'views/App/context';
import useDTBPackages from 'views/App/Portal/DTB/Packages/_data_source/useDTBPackages';
import DistributionFilterModal from '../_components/FilterModal';
import GridSection from 'components/_layouts/GridSection';
import PageToolbar from '_lex/_atoms/PageToolbar';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import type { DTBPackage } from 'views/App/Portal/DTB/_types/dtb';
import { isDtbPackageEligibleForAssignmentOrDistribution } from 'views/App/Portal/DTB/utils/dtbPackageEligibility';
import { FilterAlt } from '@mui/icons-material';

const defaultParams = {
  orgId: '',
  jurisdictionId: undefined,
  manualId: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  search: undefined,
};

const DistributionList: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(_.omit(defaultParams, ['orgId']));
  const [packages, setPackages] = useState<DTBPackage[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const isFiltered = useMemo(() => !_.isEmpty(_.omitBy(params, _.isEmpty)), [params]);

  const clearParams = useCallback(() => {
    setParams({ ..._.omit(defaultParams, ['orgId']) });
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return {
      orgId: currentOrganizationId,
      status: 'APPROVED',
      jurisdictionId: cleanParams.jurisdictionId,
      manualId: cleanParams.manualId,
      createdAtStart: cleanParams.createdAtStart,
      createdAtEnd: cleanParams.createdAtEnd,
      search: cleanParams.search,
    };
  }, [currentOrganizationId, params]);

  const packagesQuery = useDTBPackages(queryParams);

  useEffect(() => {
    if (packagesQuery.data && !packagesQuery.isError) {
      const fetchedPackages =
        packagesQuery.data.pages.flatMap((page: { content: DTBPackage[] }) => page.content) || [];
      setPackages(fetchedPackages);
    }
  }, [packagesQuery.data, packagesQuery.isError]);

  const updateParams = useCallback(
    (updatedParams: any) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams],
  );

  const onSearchChange = useCallback(
    (val: string) => {
      updateParams({ search: val });
    },
    [updateParams],
  );

  const columns = useMemo(
    () => ({
      name: {
        header: t('dtbs.distribution.grid.packageName', 'Package Name'),
        flex: 2,
        renderer: (params: GridCellParams) => <Typography variant='h6'>{params.row.name}</Typography>,
      },
      manual: {
        header: t('dtbs.distribution.grid.manual', 'Manual'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.manual?.name || '-'}</Typography>,
      },

      jurisdiction: {
        header: t('dtbs.library.table.jurisdiction', 'Jurisdiction'),
        flex: 1,
        renderer: (params: GridCellParams) => <Typography>{params.row.jurisdiction?.name}</Typography>,
      },

      createdAt: {
        header: t('dtbs.distribution.grid.created', 'Created'),
        flex: 1,
        renderer: (params: GridCellParams) => (
          <Typography>{params.row.createdAt ? params.row.createdAt.split('T')[0] : '-'}</Typography>
        ),
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const row = params.row as DTBPackage;
          const canDistribute = isDtbPackageEligibleForAssignmentOrDistribution(row);
          return (
          <ActionMenu
            id={`action_menu_${params.row.id}`}
            items={[
              {
                key: `distribute_${params.row.id}`,
                label: t('dtbs.distribution.actions.distribute', 'Distribute Package'),
                onClick: () => navigate(`/admin/dtbs/distribution/${params.row.id}/distribute`),
                disabled: !canDistribute,
              },
              {
                key: `history_${params.row.id}`,
                label: t('dtbs.distribution.actions.history', 'Distribution History'),
                onClick: () => navigate(`/admin/dtbs/distribution/${params.row.id}/history`),
              },
            ]}
          />
          );
        },
      },
    }),
    [t, navigate],
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('dtbs.distribution.title', 'DTB Package Distribution'),
        subtitle: t('dtbs.distribution.subTitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: 'center', flex: 1 }}
            >
              <QuickSearch
                id='distribution_search'
                initialValue={params.search || ''}
                onChange={onSearchChange}
                placeholder={t('dtbs.distribution.filters.search', 'Search packages, manual, jurisdiction')}
              />
              <Button
                variant='outlined'
                size='small'
                onClick={() => setFilterModalOpen(true)}
                startIcon={<FilterAlt />}
                className='icon-only'
              />
              {isFiltered && (
                <Button
                  color='neutral'
                  onClick={clearParams}
                  size='small'
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: '80px',
                    px: 2,
                  }}
                >
                  {t('dtbs.packages.clear')}
                </Button>
              )}
            </Stack>
          }
        />
        {packagesQuery.isLoading ? (
          <Card sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Card>
        ) : packages.length === 0 ? (
          <Card sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Stack
              spacing={3}
              alignItems='center'
            >
              <Box
                component='img'
                src='/admin/img/policy/dtbs/empty-package-placeholder.png'
                alt='Empty packages'
              />
              <Typography
                variant='subtitleLg'
                color='text.secondary'
              >
                {t('dtbs.packages.emptyStateFiltered', 'No packages match your current filters')}
              </Typography>
            </Stack>
          </Card>
        ) : (
          <GridSection
            columns={columns}
            data={packages}
            query={packagesQuery}
          />
        )}
        <DistributionFilterModal
          open={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          filters={params}
          onApply={updateParams}
        />
      </Box>
    </CommonLayout>
  );
};

export default DistributionList;
